import { useState, useEffect, useCallback } from 'react';
import { invoicesAPI, contactsAPI, emailAPI, settingsAPI, quotesAPI, recurringAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { FileOutput, Plus, Send, Trash2, Eye, Mail, AlertTriangle, UserPlus, Loader2, RefreshCw, Check, Download, FileText, ChevronDown, Clock, CircleDot, CircleCheck, Ban, CircleAlert, FileCheck, Upload, Repeat, Play, Pause, Pencil } from 'lucide-react';
import { Switch } from '../components/ui/switch';
import { RecurringSettings, emptyRecurring } from '../components/RecurringSettings';
import { toast } from 'sonner';

const formatCurrency = (val) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(val || 0);

const STATUS_CONFIG = {
  draft:     { label: 'Entwurf',   icon: FileText,    className: 'bg-slate-500/10 text-slate-400 border-slate-500/20' },
  open:      { label: 'Offen',     icon: CircleDot,   className: 'bg-blue-500/10 text-blue-400 border-blue-500/20' },
  sent:      { label: 'Versendet', icon: Send,        className: 'bg-indigo-500/10 text-indigo-400 border-indigo-500/20' },
  paid:      { label: 'Bezahlt',   icon: CircleCheck,  className: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' },
  overdue:   { label: 'Überfällig',icon: CircleAlert,  className: 'bg-red-500/10 text-red-400 border-red-500/20' },
  cancelled: { label: 'Storniert', icon: Ban,          className: 'bg-neutral-500/10 text-neutral-400 border-neutral-500/20' },
};

function StatusBadge({ status }) {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
  const Icon = config.icon;
  return (
    <Badge className={`text-xs ${config.className}`} data-testid={`status-badge-${status}`}>
      <Icon className="w-3 h-3 mr-1" />
      {config.label}
    </Badge>
  );
}

export default function OutgoingInvoicesPage() {
  const [invoices, setInvoices] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [companySettings, setCompanySettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showEmail, setShowEmail] = useState(null);  const [showPreview, setShowPreview] = useState(null);
  const [showInlineContact, setShowInlineContact] = useState(false);
  const [inlineContact, setInlineContact] = useState({ name: '', email: '', company: '', phone: '', contact_type: 'customer' });
  const [savingContact, setSavingContact] = useState(false);
  const [syncingId, setSyncingId] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);
  const [refreshingStatuses, setRefreshingStatuses] = useState(false);
  const [emailAddr, setEmailAddr] = useState('');
  const [sending, setSending] = useState(false);
  const [availableQuotes, setAvailableQuotes] = useState([]);
  const [showFromQuote, setShowFromQuote] = useState(false);
  const [convertingQuoteId, setConvertingQuoteId] = useState(null);
  const [recurringList, setRecurringList] = useState([]);
  const [recurring, setRecurring] = useState({ ...emptyRecurring });
  const [convertInvoice, setConvertInvoice] = useState(null);
  const [convertRecurring, setConvertRecurring] = useState({ ...emptyRecurring, enabled: true });
  const [recurringBusyId, setRecurringBusyId] = useState(null);
  const [savingRecurring, setSavingRecurring] = useState(false);
  const [editTpl, setEditTpl] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [form, setForm] = useState({
    contact_id: '', contact_name: '', invoice_date: new Date().toISOString().split('T')[0],
    due_date: '', items: [{ description: '', quantity: 1, unit_price: 0 }], tax_rate: 19, notes: '',
    price_mode: 'gross', invoice_number: '',
  });

  const loadData = useCallback(async () => {
    try {
      const [invRes, contactRes, settingsRes, quotesRes, recRes] = await Promise.all([
        invoicesAPI.outgoing.list(),
        contactsAPI.list(),
        settingsAPI.get(),
        quotesAPI.list(),
        recurringAPI.list(),
      ]);
      setInvoices(invRes.data.invoices || []);
      setRecurringList(recRes.data.recurring || []);
      setContacts(contactRes.data.contacts || []);
      setCompanySettings(settingsRes.data);
      // Filter quotes that can be converted (not already converted)
      const allQuotes = quotesRes.data.quotes || [];
      setAvailableQuotes(allQuotes.filter(q => q.status !== 'converted'));
      const s = settingsRes.data;
      if (s?.tax_type === 'kleinunternehmer') {
        setForm(prev => ({ ...prev, tax_rate: 0 }));
      }
      if (s?.default_payment_term_days) {
        const dueDate = new Date();
        dueDate.setDate(dueDate.getDate() + (s.default_payment_term_days || 14));
        setForm(prev => ({ ...prev, due_date: dueDate.toISOString().split('T')[0] }));
      }
    } catch {
      toast.error('Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // Pre-fill recipient email from the invoice's stored contact_email whenever the send-email dialog opens
  useEffect(() => {
    if (showEmail) {
      setEmailAddr(showEmail.contact_email || '');
    }
  }, [showEmail]);

  const addItem = () => setForm({ ...form, items: [...form.items, { description: '', quantity: 1, unit_price: 0 }] });
  const removeItem = (i) => setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) });
  const updateItem = (i, field, value) => {
    const items = [...form.items];
    items[i] = { ...items[i], [field]: field === 'description' ? value : Number(value) };
    setForm({ ...form, items });
  };

  // Calculation respects price_mode:
  // - 'net' (default): items.unit_price is net; tax is added on top.
  // - 'gross': items.unit_price is gross; net is extracted by dividing by (1 + tax_rate/100).
  const rawTotal = form.items.reduce((s, it) => s + it.quantity * it.unit_price, 0);
  const netTotal = form.price_mode === 'gross'
    ? rawTotal / (1 + form.tax_rate / 100)
    : rawTotal;
  const taxAmount = netTotal * (form.tax_rate / 100);
  const grossTotal = netTotal + taxAmount;

  const announceRecurring = (data) => {
    const rec = data.recurring;
    const run = data.first_run;
    if (run) {
      if (run.emailed) {
        toast.success(`Dauerrechnung aktiv – ${run.invoice_number} wurde direkt versendet, nächste am ${rec.next_run_date}`);
      } else {
        toast.warning(`${run.invoice_number} wurde angelegt, aber nicht versendet${run.error ? `: ${run.error}` : ''} – bitte manuell per E-Mail senden`);
      }
    } else {
      toast.success(`Dauerrechnung aktiv (${rec.interval_label}), erste Rechnung am ${rec.next_run_date}`);
    }
  };

  const handleConvertToRecurring = async () => {
    if (!convertInvoice) return;
    setSavingRecurring(true);
    try {
      const { enabled, ...payload } = convertRecurring;
      const res = await recurringAPI.createFromInvoice(convertInvoice.id, payload);
      announceRecurring(res.data);
      setConvertInvoice(null);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Dauerrechnung konnte nicht eingerichtet werden');
    } finally {
      setSavingRecurring(false);
    }
  };

  const openEditRecurring = (tpl) => {
    setEditTpl(tpl);
    setEditForm({
      contact_id: tpl.contact_id || '',
      contact_name: tpl.contact_name || '',
      items: (tpl.items || []).map((i) => ({
        description: i.description || '',
        quantity: Number(i.quantity ?? 1),
        unit_price: Number(i.unit_price ?? 0),
      })),
      tax_rate: Number(tpl.tax_rate ?? 19),
      notes: tpl.notes || '',
      auto_send: tpl.auto_send !== false,
      interval_unit: tpl.interval_unit || 'months',
      interval_value: Number(tpl.interval_value || 1),
      start_date: tpl.next_run_date || tpl.start_date || new Date().toISOString().split('T')[0],
      end_mode: tpl.end_mode || 'never',
      end_date: tpl.end_date || '',
      max_occurrences: Number(tpl.max_occurrences || 12),
      add_period_text: tpl.add_period_text !== false,
      enabled: true,
    });
  };

  const handleSaveRecurringEdit = async () => {
    if (!editTpl || !editForm) return;
    if (!editForm.items.length || editForm.items.some((i) => !i.description)) {
      toast.error('Bitte alle Positionen mit einer Beschreibung versehen');
      return;
    }
    setSavingRecurring(true);
    try {
      const payload = {
        contact_id: editForm.contact_id,
        contact_name: editForm.contact_name,
        items: editForm.items,
        tax_rate: Number(editForm.tax_rate),
        notes: editForm.notes,
        auto_send: editForm.auto_send,
        interval_unit: editForm.interval_unit,
        interval_value: Number(editForm.interval_value),
        end_mode: editForm.end_mode,
        end_date: editForm.end_mode === 'date' ? editForm.end_date : null,
        max_occurrences: editForm.end_mode === 'count' ? Number(editForm.max_occurrences) : null,
        add_period_text: editForm.add_period_text,
        next_run_date: editForm.start_date,
      };
      const res = await recurringAPI.update(editTpl.id, payload);
      toast.success(`Dauerrechnung gespeichert – nächste Rechnung am ${res.data.recurring.next_run_date || '–'}`);
      setEditTpl(null);
      setEditForm(null);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally {
      setSavingRecurring(false);
    }
  };

  const handleToggleRecurring = async (tpl) => {
    setRecurringBusyId(tpl.id);
    try {
      const status = tpl.status === 'active' ? 'paused' : 'active';
      await recurringAPI.update(tpl.id, { status });
      toast.success(status === 'active' ? 'Dauerrechnung fortgesetzt' : 'Dauerrechnung pausiert');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Änderung fehlgeschlagen');
    } finally {
      setRecurringBusyId(null);
    }
  };

  const handleRunRecurringNow = async (tpl) => {
    setRecurringBusyId(tpl.id);
    try {
      const res = await recurringAPI.runNow(tpl.id);
      if (res.data.emailed) {
        toast.success(`${res.data.invoice_number} erstellt und versendet – nächste am ${res.data.next_run_date || '–'}`);
      } else {
        toast.warning(`${res.data.invoice_number} als Entwurf angelegt, E-Mail nicht versendet${res.data.error ? `: ${res.data.error}` : ''}`);
      }
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ausführen fehlgeschlagen');
    } finally {
      setRecurringBusyId(null);
    }
  };

  const handleDeleteRecurring = async (tpl) => {
    if (!window.confirm('Dauerrechnung wirklich beenden? Bereits erstellte Rechnungen bleiben erhalten.')) return;
    setRecurringBusyId(tpl.id);
    try {
      await recurringAPI.remove(tpl.id);
      toast.success('Dauerrechnung beendet');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Löschen fehlgeschlagen');
    } finally {
      setRecurringBusyId(null);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.contact_name) { toast.error('Bitte Kontakt auswählen'); return; }
    try {
      // Backend always stores net unit_price. If user entered gross prices,
      // convert each item back to net before sending.
      const factor = 1 + (form.tax_rate / 100);
      const itemsForBackend = form.price_mode === 'gross' && factor > 0
        ? form.items.map((it) => ({
            ...it,
            unit_price: Number((Number(it.unit_price) / factor).toFixed(4)),
          }))
        : form.items;
      const { price_mode, ...rest } = form;
      const created = await invoicesAPI.outgoing.create({ ...rest, items: itemsForBackend, status: 'draft' });
      toast.success('Rechnung erstellt');
      if (recurring.enabled) {
        const newId = created.data?.invoice?.id;
        try {
          const { enabled, ...recPayload } = recurring;
          const res = await recurringAPI.createFromInvoice(newId, {
            ...recPayload,
            // Die soeben erstellte Rechnung ist der erste Lauf, falls der Start heute ist —
            // sonst entstünde für denselben Zeitraum ein zweites Dokument.
            use_source_as_first: true,
          });
          announceRecurring(res.data);
        } catch (recErr) {
          toast.error(recErr.response?.data?.detail || 'Dauerrechnung konnte nicht eingerichtet werden');
        }
      }
      setRecurring({ ...emptyRecurring });
      setShowCreate(false);
      setForm({
        contact_id: '', contact_name: '', invoice_date: new Date().toISOString().split('T')[0],
        due_date: '', items: [{ description: '', quantity: 1, unit_price: 0 }], tax_rate: 19, notes: '',
        price_mode: 'gross', invoice_number: '',
      });
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Erstellen');
    }
  };

  const handleSendEmail = async () => {
    if (!emailAddr || !showEmail) return;
    setSending(true);
    const inv = showEmail;
    try {
      // Backend generates the PDF and attaches it to the email — no HTML crafting needed here.
      await invoicesAPI.outgoing.sendEmail(inv.id, { recipient_email: emailAddr });
      toast.success(`Rechnung ${inv.invoice_number} mit PDF an ${emailAddr} gesendet`);
      setShowEmail(null);
      setEmailAddr('');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'E-Mail-Fehler');
    } finally {
      setSending(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await invoicesAPI.outgoing.remove(id);
      toast.success('Rechnung gelöscht');
      loadData();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Fehler beim Löschen';
      toast.error(msg);
    }
  };

  const handleCancel = async (id) => {
    try {
      const res = await invoicesAPI.outgoing.cancel(id);
      toast.success(`Rechnung storniert → ${res.data.cancel_number}`);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Storno fehlgeschlagen');
    }
  };

  const selectContact = (contactId) => {
    const c = contacts.find(c => c.id === contactId);
    setForm({ ...form, contact_id: contactId, contact_name: c ? c.name : '' });
  };

  const handleInlineContactSave = async () => {
    if (!inlineContact.name.trim()) {
      toast.error('Bitte Namen eingeben');
      return;
    }
    setSavingContact(true);
    try {
      const res = await contactsAPI.create(inlineContact);
      const newContact = res.data.contact;
      toast.success(`Kontakt "${newContact.name}" erstellt`);
      setContacts(prev => [newContact, ...prev]);
      setForm(prev => ({ ...prev, contact_id: newContact.id, contact_name: newContact.name }));
      setShowInlineContact(false);
      setInlineContact({ name: '', email: '', company: '', phone: '', contact_type: 'customer' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Erstellen');
    } finally {
      setSavingContact(false);
    }
  };

  const handleConvertQuote = async (quoteId) => {
    setConvertingQuoteId(quoteId);
    try {
      const res = await quotesAPI.convertToInvoice(quoteId);
      const inv = res.data.invoice;
      toast.success(`Rechnung ${inv.invoice_number} aus Angebot erstellt`);
      setShowFromQuote(false);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Konvertieren');
    } finally { setConvertingQuoteId(null); }
  };

  const handleRefreshStatuses = async () => {
    setRefreshingStatuses(true);
    try {
      const res = await invoicesAPI.outgoing.refreshStatuses();
      const overdueFlipped = res.data?.overdue?.flipped || 0;
      const molliePaid = res.data?.mollie?.marked_paid || 0;
      const mollieOverdue = res.data?.mollie?.marked_overdue || 0;
      const dunningSent = res.data?.dunning?.sent || 0;
      const totalChanges = overdueFlipped + molliePaid + mollieOverdue + dunningSent;
      if (totalChanges === 0) {
        toast.info('Alle Rechnungsstatus sind aktuell');
      } else {
        const parts = [];
        if (overdueFlipped) parts.push(`${overdueFlipped} überfällig`);
        if (molliePaid) parts.push(`${molliePaid} bezahlt (Mollie)`);
        if (mollieOverdue) parts.push(`${mollieOverdue} Mollie-überfällig`);
        if (dunningSent) parts.push(`${dunningSent} Erinnerung gesendet`);
        toast.success(`Aktualisiert: ${parts.join(', ')}`);
        loadData();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Aktualisierung fehlgeschlagen');
    } finally {
      setRefreshingStatuses(false);
    }
  };

  const handleDownloadPdf = async (invoice) => {
    setDownloadingId(`pdf-${invoice.id}`);
    try {
      const res = await invoicesAPI.outgoing.downloadPdf(invoice.id);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      if (isMobile) {
        window.open(url, '_blank');
      } else {
        const link = document.createElement('a');
        link.href = url;
        link.download = `${invoice.invoice_number}.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
      setTimeout(() => window.URL.revokeObjectURL(url), 5000);
      toast.success('PDF heruntergeladen');
    } catch {
      toast.error('PDF-Download fehlgeschlagen');
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDownloadXRechnung = async (invoice) => {
    setDownloadingId(`xml-${invoice.id}`);
    try {
      const res = await invoicesAPI.outgoing.downloadXRechnung(invoice.id);
      const blob = new Blob([res.data], { type: 'application/xml' });
      const url = window.URL.createObjectURL(blob);
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      if (isMobile) {
        window.open(url, '_blank');
      } else {
        const link = document.createElement('a');
        link.href = url;
        link.download = `${invoice.invoice_number}_XRechnung.xml`;
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
      setTimeout(() => window.URL.revokeObjectURL(url), 5000);
      toast.success('XRechnung heruntergeladen');
    } catch {
      toast.error('XRechnung-Download fehlgeschlagen');
    } finally {
      setDownloadingId(null);
    }
  };

  const handleStatusChange = async (invoiceId, newStatus) => {
    setStatusUpdatingId(invoiceId);
    try {
      await invoicesAPI.outgoing.updateStatus(invoiceId, newStatus);
      toast.success(`Status auf "${STATUS_CONFIG[newStatus]?.label}" geändert`);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Status-Änderung fehlgeschlagen');
    } finally {
      setStatusUpdatingId(null);
    }
  };

  return (
    <div className="space-y-8" data-testid="outgoing-invoices-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading font-extrabold text-3xl sm:text-4xl tracking-tight mb-2">Ausgangsrechnungen</h1>
          <p className="text-sm sm:text-base text-muted-foreground">Rechnungen erstellen, verwalten und versenden</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant="outline"
            onClick={handleRefreshStatuses}
            disabled={refreshingStatuses}
            className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
            data-testid="refresh-statuses-btn"
            title="Fällige Rechnungen auf überfällig setzen und Mollie-Zahlungen abfragen"
          >
            {refreshingStatuses ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Clock className="w-4 h-4 mr-2" />}
            <span className="hidden sm:inline">Status aktualisieren</span>
            <span className="sm:hidden">Status</span>
          </Button>
          <Button variant="outline" onClick={() => setShowFromQuote(true)} className="border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10" data-testid="from-quote-btn">
            <FileCheck className="w-4 h-4 mr-2" />
            <span className="hidden sm:inline">Aus Angebot erstellen</span>
            <span className="sm:hidden">Aus Angebot</span>
          </Button>
          <Button onClick={() => setShowCreate(true)} className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_15px_rgba(0,229,255,0.3)]" data-testid="create-invoice-btn">
            <Plus className="w-4 h-4 mr-2" />
            <span className="hidden sm:inline">Neue Rechnung</span>
            <span className="sm:hidden">Neu</span>
          </Button>
        </div>
      </div>

      {companySettings && !companySettings.tax_number && !companySettings.vat_id && (
        <Card className="bg-amber-500/5 border border-amber-500/30" data-testid="missing-tax-warning">
          <CardContent className="p-4 flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0" />
            <div className="text-sm">
              <span className="text-amber-400 font-semibold">Steuerinformationen fehlen: </span>
              <span className="text-muted-foreground">
                Bitte hinterlegen Sie Ihre Steuernummer oder USt-IdNr. in den{' '}
                <a href="/einstellungen" className="text-primary hover:underline">Einstellungen</a>, 
                damit Ihre Ausgangsrechnungen den gesetzlichen Anforderungen (§14 UStG) entsprechen.
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Dauerrechnungen */}
      {recurringList.length > 0 && (
        <Card className="bg-card border border-violet-500/25" data-testid="recurring-invoices-card">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-base flex items-center gap-2">
              <Repeat className="w-4 h-4 text-violet-400" />
              Dauerrechnungen
              <Badge className="bg-violet-500/10 text-violet-400 border-violet-500/20 text-xs">
                {recurringList.filter((t) => t.status === 'active').length} aktiv
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {recurringList.map((tpl) => (
              <div
                key={tpl.id}
                className="flex items-center justify-between gap-3 flex-wrap rounded-lg border border-border/30 bg-background/40 px-3 py-2.5"
                data-testid={`recurring-row-${tpl.id}`}
              >
                <div className="min-w-[220px]">
                  <p className="text-sm font-medium">{tpl.contact_name || 'Kunde'}</p>
                  <p className="text-xs text-muted-foreground font-mono-data">
                    {tpl.interval_label} · {formatCurrency((tpl.items || []).reduce((a, i) => a + (i.quantity || 0) * (i.unit_price || 0), 0) * (1 + (tpl.tax_rate || 0) / 100))} brutto
                    {tpl.end_mode === 'count' && ` · ${tpl.occurrences_created}/${tpl.max_occurrences} Rechnungen`}
                    {tpl.end_mode === 'date' && ` · bis ${tpl.end_date}`}
                    {tpl.end_mode === 'never' && ' · unbegrenzt'}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {tpl.status === 'active' && (
                    <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20 text-xs">
                      Nächste am {tpl.next_run_date}
                    </Badge>
                  )}
                  {tpl.status === 'paused' && (
                    <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/20 text-xs">Pausiert</Badge>
                  )}
                  {tpl.status === 'completed' && (
                    <Badge className="bg-slate-500/10 text-slate-400 border-slate-500/20 text-xs">Abgeschlossen</Badge>
                  )}
                  {tpl.last_invoice_number && (
                    <span className="text-[11px] text-muted-foreground font-mono-data">
                      zuletzt: {tpl.last_invoice_number}
                    </span>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRunRecurringNow(tpl)}
                    disabled={recurringBusyId === tpl.id || tpl.status === 'completed'}
                    className="h-8 text-xs text-primary"
                    title="Jetzt ausführen"
                    data-testid={`recurring-run-now-${tpl.id}`}
                  >
                    {recurringBusyId === tpl.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openEditRecurring(tpl)}
                    disabled={recurringBusyId === tpl.id}
                    className="h-8 text-xs text-muted-foreground hover:text-foreground"
                    title="Dauerrechnung bearbeiten"
                    data-testid={`recurring-edit-${tpl.id}`}
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  {tpl.status !== 'completed' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleToggleRecurring(tpl)}
                      disabled={recurringBusyId === tpl.id}
                      className="h-8 text-xs text-amber-400"
                      title={tpl.status === 'active' ? 'Pausieren' : 'Fortsetzen'}
                      data-testid={`recurring-toggle-${tpl.id}`}
                    >
                      {tpl.status === 'active' ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteRecurring(tpl)}
                    disabled={recurringBusyId === tpl.id}
                    className="h-8 text-xs text-destructive"
                    title="Dauerrechnung beenden"
                    data-testid={`recurring-delete-${tpl.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Invoice Table */}
      <Card className="bg-card border border-border/50" data-testid="outgoing-invoices-table">
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <FileOutput className="w-4 h-4 text-primary" />
            Rechnungen ({invoices.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-12 text-center text-muted-foreground">Laden...</div>
          ) : invoices.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground">
              <FileOutput className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>Noch keine Ausgangsrechnungen</p>
            </div>
          ) : (
            <div className="table-responsive">
            <Table>
              <TableHeader>
                <TableRow className="border-border/30">
                  <TableHead>Nr.</TableHead>
                  <TableHead>Kontakt</TableHead>
                  <TableHead className="hidden sm:table-cell">Datum</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Brutto</TableHead>
                  <TableHead className="text-right">Aktionen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoices.map((inv) => (
                  <TableRow key={inv.id} className="border-border/20 hover:bg-accent/50" data-testid={`outgoing-row-${inv.id}`}>
                    <TableCell className="font-mono-data text-sm text-primary">{inv.invoice_number}</TableCell>
                    <TableCell className="max-w-[120px] truncate">{inv.contact_name}</TableCell>
                    <TableCell className="font-mono-data text-sm hidden sm:table-cell">{inv.invoice_date}</TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild disabled={statusUpdatingId === inv.id}>
                          <button className="flex items-center gap-1 cursor-pointer hover:opacity-80 transition-opacity" data-testid={`status-trigger-${inv.id}`}>
                            {statusUpdatingId === inv.id ? (
                              <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" />
                            ) : (
                              <>
                                <StatusBadge status={inv.status || 'draft'} />
                                <ChevronDown className="w-3 h-3 text-muted-foreground" />
                              </>
                            )}
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" className="bg-card border-border/50">
                          {Object.entries(STATUS_CONFIG).map(([key, config]) => {
                            const Icon = config.icon;
                            return (
                              <DropdownMenuItem
                                key={key}
                                onClick={() => handleStatusChange(inv.id, key)}
                                className="cursor-pointer"
                                data-testid={`status-option-${key}`}
                              >
                                <Icon className="w-3 h-3 mr-2" />
                                {config.label}
                              </DropdownMenuItem>
                            );
                          })}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                    <TableCell className="text-right font-mono-data font-bold">{formatCurrency(inv.gross_total)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex gap-1 justify-end">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDownloadPdf(inv)}
                          disabled={downloadingId === `pdf-${inv.id}`}
                          className="text-muted-foreground hover:text-foreground"
                          title="PDF herunterladen"
                          data-testid={`download-pdf-${inv.id}`}
                        >
                          {downloadingId === `pdf-${inv.id}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDownloadXRechnung(inv)}
                          disabled={downloadingId === `xml-${inv.id}`}
                          className="text-muted-foreground hover:text-amber-400"
                          title="XRechnung XML herunterladen"
                          data-testid={`download-xrechnung-${inv.id}`}
                        >
                          {downloadingId === `xml-${inv.id}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setShowPreview(inv)} className="text-muted-foreground hover:text-foreground" data-testid={`preview-invoice-${inv.id}`}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setShowEmail(inv)} className="text-primary" data-testid={`email-invoice-${inv.id}`}>
                          <Mail className="w-4 h-4" />
                        </Button>
                        {!inv.is_storno && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setConvertRecurring({ ...emptyRecurring, enabled: true, start_date: new Date().toISOString().split('T')[0] });
                              setConvertInvoice(inv);
                            }}
                            className="text-violet-400 hover:text-violet-300"
                            title="Als Dauerrechnung einrichten"
                            data-testid={`recurring-from-invoice-${inv.id}`}
                          >
                            <Repeat className="w-4 h-4" />
                          </Button>
                        )}
                        {inv.status === 'draft' && (
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(inv.id)} className="text-destructive" data-testid={`delete-outgoing-${inv.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                        {inv.status !== 'draft' && inv.status !== 'cancelled' && (
                          <Button variant="ghost" size="sm" onClick={() => handleCancel(inv.id)} className="text-amber-500 hover:text-amber-400" title="Stornieren" data-testid={`cancel-outgoing-${inv.id}`}>
                            <Ban className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Invoice Dialog */}
      {showCreate && (
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-card border-border/50 max-w-2xl max-h-[90vh] overflow-y-auto w-[95vw] sm:w-auto" data-testid="create-invoice-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg sm:text-xl">Neue Ausgangsrechnung</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Kontakt</Label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-6 text-xs text-primary hover:text-primary/80 px-2"
                    onClick={() => setShowInlineContact(!showInlineContact)}
                    data-testid="toggle-inline-contact-btn"
                  >
                    <UserPlus className="w-3 h-3 mr-1" />
                    {showInlineContact ? 'Abbrechen' : 'Neuer Kontakt'}
                  </Button>
                </div>

                {!showInlineContact ? (
                  <>
                    <Select value={form.contact_id} onValueChange={selectContact}>
                      <SelectTrigger className="bg-background h-10" data-testid="contact-select">
                        <SelectValue placeholder="Kontakt wählen" />
                      </SelectTrigger>
                      <SelectContent>
                        {contacts.map(c => (
                          <SelectItem key={c.id} value={c.id}>{c.name}{c.company ? ` (${c.company})` : ''}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {contacts.length === 0 && (
                      <p className="text-xs text-amber-400">
                        Keine Kontakte vorhanden. Erstellen Sie einen neuen Kontakt.
                      </p>
                    )}
                  </>
                ) : (
                  <div className="bg-accent/50 rounded-lg p-3 space-y-3 border border-primary/20" data-testid="inline-contact-form">
                    <div className="grid grid-cols-2 gap-2">
                      <div className="col-span-2 space-y-1">
                        <Label className="text-xs">Name *</Label>
                        <Input
                          value={inlineContact.name}
                          onChange={(e) => setInlineContact({ ...inlineContact, name: e.target.value })}
                          placeholder="Max Mustermann"
                          className="bg-background h-8 text-sm"
                          data-testid="inline-contact-name"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Firma</Label>
                        <Input
                          value={inlineContact.company}
                          onChange={(e) => setInlineContact({ ...inlineContact, company: e.target.value })}
                          placeholder="Firma GmbH"
                          className="bg-background h-8 text-sm"
                          data-testid="inline-contact-company"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">E-Mail</Label>
                        <Input
                          type="email"
                          value={inlineContact.email}
                          onChange={(e) => setInlineContact({ ...inlineContact, email: e.target.value })}
                          placeholder="name@firma.de"
                          className="bg-background h-8 text-sm"
                          data-testid="inline-contact-email"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Telefon</Label>
                        <Input
                          value={inlineContact.phone}
                          onChange={(e) => setInlineContact({ ...inlineContact, phone: e.target.value })}
                          placeholder="+49..."
                          className="bg-background h-8 text-sm"
                          data-testid="inline-contact-phone"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Typ</Label>
                        <Select value={inlineContact.contact_type} onValueChange={(v) => setInlineContact({ ...inlineContact, contact_type: v })}>
                          <SelectTrigger className="bg-background h-8 text-sm" data-testid="inline-contact-type">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="customer">Kunde</SelectItem>
                            <SelectItem value="vendor">Lieferant</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      onClick={handleInlineContactSave}
                      disabled={savingContact || !inlineContact.name.trim()}
                      className="w-full bg-primary text-primary-foreground hover:bg-primary/90 h-8 text-sm"
                      data-testid="save-inline-contact-btn"
                    >
                      {savingContact ? (
                        <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Wird erstellt...</>
                      ) : (
                        <><UserPlus className="w-3 h-3 mr-1" /> Kontakt erstellen & auswählen</>
                      )}
                    </Button>
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label>USt-Satz</Label>
                <Select value={String(form.tax_rate)} onValueChange={(v) => setForm({ ...form, tax_rate: Number(v) })}>
                  <SelectTrigger className="bg-background h-10" data-testid="tax-rate-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="19">19%</SelectItem>
                    <SelectItem value="7">7%</SelectItem>
                    <SelectItem value="0">0% (steuerfrei)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Preise eingeben als</Label>
                <Select value={form.price_mode} onValueChange={(v) => setForm({ ...form, price_mode: v })}>
                  <SelectTrigger className="bg-background h-10" data-testid="price-mode-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="net" data-testid="price-mode-net">Netto (zzgl. USt)</SelectItem>
                    <SelectItem value="gross" data-testid="price-mode-gross">Brutto (inkl. USt)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Rechnungsdatum</Label>
                <Input type="date" value={form.invoice_date} onChange={(e) => setForm({ ...form, invoice_date: e.target.value })} className="bg-background h-10" data-testid="invoice-date-input" />
              </div>
              <div className="space-y-2">
                <Label>Fälligkeitsdatum</Label>
                <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="bg-background h-10" data-testid="due-date-input" />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label>Rechnungsnummer <span className="text-xs text-muted-foreground font-normal">(optional, leer = automatisch)</span></Label>
                <Input
                  value={form.invoice_number}
                  onChange={(e) => setForm({ ...form, invoice_number: e.target.value })}
                  placeholder="z.B. RE-2026-0042 (leer lassen für automatische Vergabe)"
                  className="bg-background h-10 font-mono-data"
                  data-testid="invoice-number-input"
                />
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium uppercase tracking-wider text-muted-foreground">Positionen</Label>
                <Button type="button" variant="outline" size="sm" onClick={addItem} data-testid="add-item-btn">
                  <Plus className="w-3 h-3 mr-1" /> Position
                </Button>
              </div>
              {form.items.map((item, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-end">
                  <div className="col-span-12 sm:col-span-6">
                    <Input placeholder="Beschreibung" value={item.description} onChange={(e) => updateItem(i, 'description', e.target.value)} className="bg-background h-9 text-sm" data-testid={`item-desc-${i}`} />
                  </div>
                  <div className="col-span-4 sm:col-span-2">
                    <Input type="number" min="1" value={item.quantity} onChange={(e) => updateItem(i, 'quantity', e.target.value)} className="bg-background h-9 text-sm font-mono-data" data-testid={`item-qty-${i}`} />
                  </div>
                  <div className="col-span-6 sm:col-span-3">
                    <Input type="number" step="0.01" min="0" value={item.unit_price} onChange={(e) => updateItem(i, 'unit_price', e.target.value)} className="bg-background h-9 text-sm font-mono-data" data-testid={`item-price-${i}`} placeholder={form.price_mode === 'gross' ? 'Brutto €' : 'Netto €'} />
                  </div>
                  <div className="col-span-2 sm:col-span-1">
                    {form.items.length > 1 && (
                      <Button type="button" variant="ghost" size="sm" onClick={() => removeItem(i)} className="text-destructive h-9 w-9 p-0" data-testid={`remove-item-${i}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="space-y-2">
              <Label>Notizen</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Optionale Bemerkungen..." className="bg-background" data-testid="invoice-notes-input" />
            </div>

            <RecurringSettings value={recurring} onChange={setRecurring} />

            <div className="bg-accent/50 rounded-lg p-4 space-y-2">
              {form.price_mode === 'gross' && form.tax_rate > 0 && (
                <div className="text-xs text-muted-foreground italic pb-1 border-b border-border/30">
                  Eingegebene Bruttopreise — USt wird herausgerechnet.
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Netto</span>
                <span className="font-mono-data">{formatCurrency(netTotal)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">USt ({form.tax_rate}%)</span>
                <span className="font-mono-data">{formatCurrency(taxAmount)}</span>
              </div>
              <div className="flex justify-between font-bold border-t border-border/30 pt-2 mt-2">
                <span>Brutto</span>
                <span className="font-mono-data text-primary text-lg">{formatCurrency(grossTotal)}</span>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreate(false)}>Abbrechen</Button>
              <Button type="submit" className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="save-invoice-btn">
                <FileOutput className="w-4 h-4 mr-2" />
                Rechnung erstellen
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      )}

      {/* Dauerrechnung aus bestehender Rechnung */}
      <Dialog open={!!convertInvoice} onOpenChange={(open) => !open && setConvertInvoice(null)}>
        <DialogContent className="max-w-[95vw] sm:max-w-2xl" data-testid="convert-recurring-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Repeat className="w-4 h-4 text-violet-400" />
              Dauerrechnung einrichten
            </DialogTitle>
          </DialogHeader>
          {convertInvoice && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Positionen und Kunde werden aus <span className="font-mono-data text-foreground">{convertInvoice.invoice_number}</span>{' '}
                ({convertInvoice.contact_name}) übernommen. Jede neue Rechnung wird automatisch erstellt und
                {convertInvoice.contact_email ? ` an ${convertInvoice.contact_email}` : ' an die Kundenadresse'} versendet.
              </p>
              <RecurringSettings value={convertRecurring} onChange={setConvertRecurring} showToggle={false} />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConvertInvoice(null)}>Abbrechen</Button>
            <Button
              onClick={handleConvertToRecurring}
              disabled={savingRecurring}
              className="bg-violet-500/90 text-white hover:bg-violet-500"
              data-testid="save-recurring-btn"
            >
              {savingRecurring ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Repeat className="w-4 h-4 mr-2" />}
              Dauerrechnung aktivieren
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dauerrechnung bearbeiten */}
      {editTpl && editForm && (
      <Dialog open={!!editTpl} onOpenChange={(open) => { if (!open) { setEditTpl(null); setEditForm(null); } }}>
        <DialogContent className="max-w-[95vw] sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="edit-recurring-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Repeat className="w-4 h-4 text-violet-400" />
              Dauerrechnung bearbeiten
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-5">
            <p className="text-xs text-muted-foreground">
              Bereits erstellte Rechnungen bleiben unverändert. Änderungen gelten ab der nächsten Rechnung
              {editTpl.occurrences_created > 0 && ` (bisher ${editTpl.occurrences_created} erstellt, zuletzt ${editTpl.last_invoice_number || '–'})`}.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs">Kunde</Label>
                <Select
                  value={editForm.contact_id}
                  onValueChange={(v) => {
                    const c = contacts.find((x) => x.id === v);
                    setEditForm({ ...editForm, contact_id: v, contact_name: c?.name || '' });
                  }}
                >
                  <SelectTrigger className="bg-background h-10" data-testid="edit-recurring-contact-select">
                    <SelectValue placeholder="Kunde wählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {contacts.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}{c.company ? ` · ${c.company}` : ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs">Steuersatz (%)</Label>
                <Input
                  type="number"
                  min={0}
                  max={25}
                  step="0.1"
                  value={editForm.tax_rate}
                  onChange={(e) => setEditForm({ ...editForm, tax_rate: Number(e.target.value) })}
                  className="bg-background h-10"
                  data-testid="edit-recurring-tax-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs">Positionen (Nettopreise)</Label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => setEditForm({ ...editForm, items: [...editForm.items, { description: '', quantity: 1, unit_price: 0 }] })}
                  data-testid="edit-recurring-add-item"
                >
                  <Plus className="w-3.5 h-3.5 mr-1" /> Position
                </Button>
              </div>
              {editForm.items.map((item, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`edit-recurring-item-${i}`}>
                  <Input
                    className="col-span-6 bg-background h-10"
                    placeholder="Beschreibung"
                    value={item.description}
                    onChange={(e) => {
                      const items = [...editForm.items];
                      items[i] = { ...items[i], description: e.target.value };
                      setEditForm({ ...editForm, items });
                    }}
                    data-testid={`edit-recurring-item-desc-${i}`}
                  />
                  <Input
                    className="col-span-2 bg-background h-10"
                    type="number"
                    min={0}
                    step="0.01"
                    value={item.quantity}
                    onChange={(e) => {
                      const items = [...editForm.items];
                      items[i] = { ...items[i], quantity: Number(e.target.value) };
                      setEditForm({ ...editForm, items });
                    }}
                    data-testid={`edit-recurring-item-qty-${i}`}
                  />
                  <Input
                    className="col-span-3 bg-background h-10"
                    type="number"
                    min={0}
                    step="0.01"
                    value={item.unit_price}
                    onChange={(e) => {
                      const items = [...editForm.items];
                      items[i] = { ...items[i], unit_price: Number(e.target.value) };
                      setEditForm({ ...editForm, items });
                    }}
                    data-testid={`edit-recurring-item-price-${i}`}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="col-span-1 h-10 text-destructive"
                    disabled={editForm.items.length === 1}
                    onClick={() => setEditForm({ ...editForm, items: editForm.items.filter((_, idx) => idx !== i) })}
                    data-testid={`edit-recurring-item-remove-${i}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ))}
              <p className="text-xs text-muted-foreground font-mono-data text-right" data-testid="edit-recurring-total">
                {formatCurrency(editForm.items.reduce((a, i) => a + (i.quantity || 0) * (i.unit_price || 0), 0) * (1 + (editForm.tax_rate || 0) / 100))} brutto je Rechnung
              </p>
            </div>

            <div className="space-y-2">
              <Label className="text-xs">Notiz auf der Rechnung</Label>
              <Textarea
                value={editForm.notes}
                onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                className="bg-background min-h-[70px]"
                data-testid="edit-recurring-notes-input"
              />
            </div>

            <RecurringSettings
              value={editForm}
              onChange={setEditForm}
              showToggle={false}
              startLabel="Nächste Rechnung am"
            />

            <div className="flex items-center justify-between gap-4 rounded-lg border border-border/30 px-3 py-2.5">
              <Label className="text-xs text-muted-foreground">
                Rechnung automatisch per E-Mail an den Kunden senden
              </Label>
              <Switch
                checked={editForm.auto_send}
                onCheckedChange={(v) => setEditForm({ ...editForm, auto_send: v })}
                data-testid="edit-recurring-autosend-toggle"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setEditTpl(null); setEditForm(null); }}>Abbrechen</Button>
            <Button
              onClick={handleSaveRecurringEdit}
              disabled={savingRecurring}
              className="bg-violet-500/90 text-white hover:bg-violet-500"
              data-testid="save-recurring-edit-btn"
            >
              {savingRecurring ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              Änderungen speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      )}


      {/* Email Dialog */}
      {showEmail && (
      <Dialog open={!!showEmail} onOpenChange={() => setShowEmail(null)}>
        <DialogContent className="bg-card border-border/50" data-testid="email-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Rechnung per E-Mail senden</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Rechnung <span className="text-primary font-mono-data">{showEmail?.invoice_number}</span> an Kunden senden
            </p>
            <div className="space-y-2">
              <Label>Empfänger E-Mail</Label>
              <Input type="email" placeholder="kunde@firma.de" value={emailAddr} onChange={(e) => setEmailAddr(e.target.value)} className="bg-background h-10" data-testid="email-address-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEmail(null)}>Abbrechen</Button>
            <Button onClick={handleSendEmail} disabled={sending || !emailAddr} className="bg-primary text-primary-foreground" data-testid="send-email-btn">
              <Send className="w-4 h-4 mr-2" />
              {sending ? 'Wird gesendet...' : 'Senden'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      )}

      {/* Invoice Preview Dialog */}
      {showPreview && (
      <Dialog open={!!showPreview} onOpenChange={() => setShowPreview(null)}>
        <DialogContent className="bg-card border-border/50 max-w-2xl max-h-[90vh] overflow-y-auto w-[95vw] sm:w-auto" data-testid="invoice-preview-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-3">
              Rechnungsvorschau: {showPreview?.invoice_number}
              <StatusBadge status={showPreview?.status || 'draft'} />
            </DialogTitle>
          </DialogHeader>

          <div className="bg-background rounded-lg border border-border/30 p-8 space-y-6">
            {/* Company Header */}
            <div className="flex justify-between items-start">
              <div>
                <h3 className="font-heading font-bold text-xl text-primary">{companySettings?.company_name || 'Firmenname'}</h3>
                {companySettings?.name && <p className="text-sm text-muted-foreground">{companySettings.name}</p>}
                {companySettings?.street && <p className="text-sm text-muted-foreground">{companySettings.street}</p>}
                {(companySettings?.zip_code || companySettings?.city) && (
                  <p className="text-sm text-muted-foreground">{companySettings.zip_code} {companySettings.city}</p>
                )}
              </div>
              <div className="text-right text-sm">
                {companySettings?.tax_number && (
                  <p className="text-muted-foreground">Steuernr.: <span className="font-mono-data">{companySettings.tax_number}</span></p>
                )}
                {companySettings?.vat_id && (
                  <p className="text-muted-foreground">USt-IdNr.: <span className="font-mono-data">{companySettings.vat_id}</span></p>
                )}
                {!companySettings?.tax_number && !companySettings?.vat_id && (
                  <p className="text-destructive text-xs flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Steuer-ID fehlt!</p>
                )}
              </div>
            </div>

            <Separator className="bg-border/30" />

            <div className="grid grid-cols-2 gap-6">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Empfänger</p>
                <p className="font-semibold" data-testid="preview-contact-name">{showPreview.contact_name}</p>
                {showPreview.contact_company && (
                  <p className="text-sm" data-testid="preview-contact-company">{showPreview.contact_company}</p>
                )}
                {showPreview.contact_address && (
                  <p className="text-sm text-muted-foreground" data-testid="preview-contact-address">{showPreview.contact_address}</p>
                )}
                {(showPreview.contact_zip_code || showPreview.contact_city) && (
                  <p className="text-sm text-muted-foreground" data-testid="preview-contact-city">
                    {[showPreview.contact_zip_code, showPreview.contact_city].filter(Boolean).join(' ')}
                  </p>
                )}
                {showPreview.contact_country && !['DE', 'Deutschland', 'Germany'].includes(showPreview.contact_country) && (
                  <p className="text-sm text-muted-foreground">{showPreview.contact_country}</p>
                )}
              </div>
              <div className="text-right space-y-1">
                <p className="text-sm"><span className="text-muted-foreground">Rechnungsnr.:</span> <span className="font-mono-data text-primary">{showPreview.invoice_number}</span></p>
                <p className="text-sm"><span className="text-muted-foreground">Datum:</span> <span className="font-mono-data">{showPreview.invoice_date}</span></p>
                <p className="text-sm"><span className="text-muted-foreground">Fällig:</span> <span className="font-mono-data">{showPreview.due_date || 'Sofort'}</span></p>
                {showPreview.from_quote_number && (
                  <p className="text-xs text-cyan-400 mt-1">Aus Angebot: {showPreview.from_quote_number}</p>
                )}
              </div>
            </div>

            <Table>
              <TableHeader>
                <TableRow className="border-border/30">
                  <TableHead className="text-xs">Pos.</TableHead>
                  <TableHead className="text-xs">Beschreibung</TableHead>
                  <TableHead className="text-xs text-right">Menge</TableHead>
                  <TableHead className="text-xs text-right">Einzelpreis</TableHead>
                  <TableHead className="text-xs text-right">Gesamt</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(showPreview.items || []).map((item, i) => (
                  <TableRow key={i} className="border-border/20">
                    <TableCell className="font-mono-data text-sm">{i + 1}</TableCell>
                    <TableCell className="text-sm">{item.description}</TableCell>
                    <TableCell className="text-right font-mono-data text-sm">{item.quantity}</TableCell>
                    <TableCell className="text-right font-mono-data text-sm">{formatCurrency(item.unit_price)}</TableCell>
                    <TableCell className="text-right font-mono-data text-sm font-bold">{formatCurrency(item.quantity * item.unit_price)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <div className="bg-accent/50 rounded-lg p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Netto</span>
                <span className="font-mono-data">{formatCurrency(showPreview.net_total)}</span>
              </div>
              {showPreview.tax_rate > 0 ? (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">USt ({showPreview.tax_rate}%)</span>
                  <span className="font-mono-data">{formatCurrency(showPreview.tax_amount)}</span>
                </div>
              ) : (
                <div className="text-xs text-muted-foreground italic">
                  Gemäß §19 UStG wird keine Umsatzsteuer berechnet.
                </div>
              )}
              <div className="flex justify-between text-lg font-bold border-t border-border/30 pt-2 mt-2">
                <span>Gesamtbetrag</span>
                <span className="font-mono-data text-primary">{formatCurrency(showPreview.gross_total)}</span>
              </div>
            </div>

            {companySettings?.iban && (
              <div className="bg-accent/30 rounded-lg p-4 space-y-1 text-sm">
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Bankverbindung</p>
                {companySettings.bank_name && <p>Bank: <span className="font-medium">{companySettings.bank_name}</span></p>}
                <p>IBAN: <span className="font-mono-data font-medium">{companySettings.iban}</span></p>
                {companySettings.bic && <p>BIC: <span className="font-mono-data font-medium">{companySettings.bic}</span></p>}
              </div>
            )}

            {showPreview.notes && (
              <p className="text-sm text-muted-foreground">{showPreview.notes}</p>
            )}
            {companySettings?.invoice_footer_text && (
              <p className="text-sm text-muted-foreground border-t border-border/20 pt-3">{companySettings.invoice_footer_text}</p>
            )}
          </div>

          <DialogFooter className="flex-wrap gap-2">
            <Button variant="outline" onClick={() => setShowPreview(null)}>Schließen</Button>
            <Button
              variant="outline"
              onClick={() => handleDownloadPdf(showPreview)}
              disabled={downloadingId === `pdf-${showPreview?.id}`}
              data-testid="preview-download-pdf-btn"
            >
              {downloadingId === `pdf-${showPreview?.id}` ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              PDF
            </Button>
            <Button
              variant="outline"
              onClick={() => handleDownloadXRechnung(showPreview)}
              disabled={downloadingId === `xml-${showPreview?.id}`}
              data-testid="preview-download-xrechnung-btn"
            >
              {downloadingId === `xml-${showPreview?.id}` ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
              XRechnung
            </Button>
            <Button
              onClick={() => { setShowPreview(null); setShowEmail(showPreview); }}
              className="bg-primary text-primary-foreground"
              data-testid="preview-send-email-btn"
            >
              <Mail className="w-4 h-4 mr-2" />
              Per E-Mail senden
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      )}

      {/* From Quote Dialog */}
      {showFromQuote && (
        <Dialog open={showFromQuote} onOpenChange={setShowFromQuote}>
          <DialogContent className="bg-card border-border/50 max-w-lg w-[95vw] sm:w-full" data-testid="from-quote-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading text-lg flex items-center gap-2">
                <FileCheck className="w-5 h-5 text-cyan-400" />
                Rechnung aus Angebot erstellen
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">Wählen Sie ein Angebot, das in eine Rechnung umgewandelt werden soll.</p>
              {availableQuotes.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4 text-center">Keine offenen Angebote vorhanden</p>
              ) : (
                <div className="space-y-2 max-h-[50vh] overflow-y-auto">
                  {availableQuotes.map(q => (
                    <div key={q.id} className="flex items-center justify-between p-3 rounded-lg bg-accent/30 border border-border/30 hover:border-cyan-500/30 transition-colors" data-testid={`from-quote-item-${q.id}`}>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium font-mono-data text-primary">{q.quote_number}</p>
                        <p className="text-xs text-muted-foreground truncate">{q.contact_name} &middot; {new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(q.gross_total)}</p>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => handleConvertQuote(q.id)}
                        disabled={convertingQuoteId === q.id}
                        className="bg-cyan-500/20 text-cyan-400 hover:bg-cyan-500/30 ml-2 flex-shrink-0"
                        data-testid={`convert-quote-btn-${q.id}`}
                      >
                        {convertingQuoteId === q.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileOutput className="w-4 h-4 mr-1" />}
                        Umwandeln
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowFromQuote(false)}>Schließen</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
